
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/components/ui/use-toast';
import { Tag, AlertCircle, Clock } from 'lucide-react';
import DiscountBadge from '@/components/DiscountBadge';
import { useBusinessDiscount } from '@/hooks/useBusinessDiscount';

interface DiscountFormData {
  discount_amount: number;
  is_percentage: boolean;
  applies_to_all: boolean;
  description?: string;
}

interface BusinessDiscountFormProps {
  businessId: string;
  onSuccess?: () => void;
  onCancel?: () => void;
}

interface DiscountCreationResult {
  success: boolean;
  auto_approved?: boolean;
  approval_reason?: string;
  error?: string;
}

const BusinessDiscountForm = ({ businessId, onSuccess, onCancel }: BusinessDiscountFormProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<DiscountFormData>({
    defaultValues: {
      discount_amount: 10,
      is_percentage: true,
      applies_to_all: true,
      description: '',
    },
  });

  const isPercentage = watch('is_percentage');

  // Get active discount for comparison
  const { data: activeDiscount } = useBusinessDiscount(businessId);

  // Check for existing pending discount
  const { data: pendingDiscount, isLoading: isPendingLoading } = useQuery({
    queryKey: ['pending-discount', businessId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('business_discounts')
        .select('*')
        .eq('business_id', businessId)
        .eq('status', 'Pending Validation')
        .maybeSingle();
      
      if (error) throw error;
      return data;
    },
  });

  const createDiscount = useMutation({
    mutationFn: async (data: DiscountFormData) => {
      const { data: result, error } = await supabase.rpc('create_business_discount', {
        p_business_id: businessId,
        p_discount_amount: data.discount_amount,
        p_is_percentage: data.is_percentage,
        p_applies_to_all: data.applies_to_all,
        p_description: data.description || null,
      });

      if (error) throw error;
      return result as DiscountCreationResult;
    },
    onSuccess: (result) => {
      if (result.success) {
        if (result.auto_approved) {
          toast({
            title: "Discount Approved Instantly! 🎉",
            description: `Your improved discount has been automatically approved and is now active. ${result.approval_reason}`,
          });
        } else {
          toast({
            title: "Discount Submitted for Review",
            description: `Your discount has been submitted for admin review. ${result.approval_reason}`,
          });
        }
        onSuccess?.();
      } else {
        toast({
          title: "Cannot Create Discount",
          description: result.error,
          variant: "destructive",
        });
      }
    },
    onError: (error) => {
      console.error('Error creating discount:', error);
      toast({
        title: "Error",
        description: "Failed to create discount. Please try again.",
        variant: "destructive",
      });
    },
  });

  const onSubmit = async (data: DiscountFormData) => {
    setIsSubmitting(true);
    try {
      await createDiscount.mutateAsync(data);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handlePercentageToggle = (checked: boolean) => {
    setValue('is_percentage', checked);
    setValue('discount_amount', checked ? 10 : 5);
  };

  const maxValue = isPercentage ? 100 : 999999;
  const minValue = isPercentage ? 1 : 0.01;

  // Show pending discount if it exists
  if (pendingDiscount) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5 text-yellow-600" />
            Discount Pending Review
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h3 className="font-medium text-yellow-800 mb-2">Discount Under Review</h3>
                <p className="text-sm text-yellow-700 mb-3">
                  You have a discount waiting for admin approval. You cannot create a new discount until this one is reviewed.
                </p>
                <div className="flex items-center gap-2">
                  <DiscountBadge
                    amount={pendingDiscount.discount_amount}
                    isPercentage={pendingDiscount.is_percentage}
                    appliesToAll={pendingDiscount.applies_to_all}
                    type={pendingDiscount.type as 'Paid' | 'Free'}
                  />
                  <span className="text-xs text-yellow-600">
                    Submitted: {new Date(pendingDiscount.created_at).toLocaleDateString()}
                  </span>
                </div>
                {pendingDiscount.description && (
                  <p className="text-sm text-yellow-700 mt-2">
                    <strong>Description:</strong> {pendingDiscount.description}
                  </p>
                )}
              </div>
            </div>
          </div>

          {activeDiscount && (
            <div>
              <h4 className="font-medium mb-2">Current Active Discount</h4>
              <DiscountBadge
                amount={activeDiscount.discount_amount}
                isPercentage={activeDiscount.is_percentage}
                appliesToAll={activeDiscount.applies_to_all}
                type={activeDiscount.type as 'Paid' | 'Free'}
              />
            </div>
          )}

          {onCancel && (
            <div className="flex justify-end pt-4">
              <Button variant="outline" onClick={onCancel}>
                Close
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  if (isPendingLoading) {
    return (
      <Card>
        <CardContent className="py-8">
          <div className="flex items-center justify-center">
            <div className="h-8 w-8 animate-spin rounded-full border-3 border-current border-t-transparent text-primary" />
            <span className="ml-3">Checking discount status...</span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Tag className="h-5 w-5" />
          Create Community Discount
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {activeDiscount && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <h4 className="font-medium text-blue-800 mb-2">Current Active Discount</h4>
              <div className="flex items-center gap-2 mb-2">
                <DiscountBadge
                  amount={activeDiscount.discount_amount}
                  isPercentage={activeDiscount.is_percentage}
                  appliesToAll={activeDiscount.applies_to_all}
                  type={activeDiscount.type as 'Paid' | 'Free'}
                />
              </div>
              <p className="text-sm text-blue-700">
                💡 <strong>Tip:</strong> If your new discount is better (higher amount with same type and coverage), it will be approved automatically!
              </p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="discount_amount">
                Discount Amount {isPercentage ? '(%)' : '(£)'}
              </Label>
              <Input
                id="discount_amount"
                type="number"
                step={isPercentage ? "1" : "0.01"}
                min={minValue}
                max={maxValue}
                {...register('discount_amount', {
                  required: 'Discount amount is required',
                  min: {
                    value: minValue,
                    message: `Minimum value is ${minValue}`,
                  },
                  max: {
                    value: maxValue,
                    message: `Maximum value is ${maxValue}`,
                  },
                })}
                className={errors.discount_amount ? 'border-red-500' : ''}
              />
              {errors.discount_amount && (
                <p className="text-sm text-red-500">{errors.discount_amount.message}</p>
              )}
            </div>

            <div className="space-y-2">
              <Label>Discount Type</Label>
              <div className="flex items-center space-x-2 p-3 border rounded-lg">
                <Switch
                  id="is_percentage"
                  checked={isPercentage}
                  onCheckedChange={handlePercentageToggle}
                />
                <Label htmlFor="is_percentage" className="cursor-pointer">
                  {isPercentage ? 'Percentage (%)' : 'Fixed Amount (£)'}
                </Label>
              </div>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Coverage</Label>
            <div className="flex items-center space-x-2 p-3 border rounded-lg">
              <Switch
                id="applies_to_all"
                checked={watch('applies_to_all')}
                onCheckedChange={(checked) => setValue('applies_to_all', checked)}
              />
              <Label htmlFor="applies_to_all" className="cursor-pointer">
                {watch('applies_to_all') ? 'All services' : 'Selected services only'}
              </Label>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="description">
              Description <span className="text-muted-foreground">(Optional)</span>
            </Label>
            <Textarea
              id="description"
              placeholder="Describe any special terms or conditions for this discount..."
              {...register('description')}
              rows={3}
            />
          </div>

          <div className="flex gap-3 pt-4">
            <Button
              type="submit"
              disabled={isSubmitting || createDiscount.isPending}
              className="flex-1"
            >
              {isSubmitting ? 'Creating...' : 'Create Discount'}
            </Button>
            {onCancel && (
              <Button
                type="button"
                variant="outline"
                onClick={onCancel}
                disabled={isSubmitting}
              >
                Cancel
              </Button>
            )}
          </div>
        </form>
      </CardContent>
    </Card>
  );
};

export default BusinessDiscountForm;
